import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';

import { cn } from '@poseidon/components/cn';
import { General } from '@poseidon/components/StrokeIcons';

import type { SequenceContainerStepType, SequenceStepType } from '~/components/Sequence/sequenceSteps';
import { SequenceStepPicker } from '~/components/Sequence/SequenceStepPicker';

// Stable middleware identity (these render on every pan/zoom frame). `flip` opens the menu upward when
// there's no room below; `shift` nudges it horizontally to stay on-screen — together they stop the menu
// being cropped near a viewport edge.
const PICKER_MIDDLEWARE = [offset(8), flip({ padding: 8 }), shift({ padding: 8 })];

// The full grown/blue "+" tile — shared by the active (menu-open) state and the externally-driven
// `hovered` look so the two can't drift.
const GROWN_TILE =
  'size-8 border-blue-400 bg-blue-50 text-blue-500 [&>svg]:size-5 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300';

interface Props {
  active: boolean;
  /** Another picker is open elsewhere — stay visible but non-clickable (no hover-grow). */
  inert?: boolean;
  /** Force the grown/blue hover look without the pointer being over the "+" — used so hovering the
   *  "Click + to add your first step" hint (a separate element) animates this button too. */
  hovered?: boolean;
  onOpen: () => void;
  onPick: (type: SequenceStepType) => void;
  /** When provided, the picker also offers the parallel container steps (main spine only). */
  onPickContainer?: (type: SequenceContainerStepType) => void;
  onCancel: () => void;
  disabledReason?: (type: SequenceStepType) => string | null;
}

/**
 * An insertion point on the sequence spine. At rest the dashed "+" is half-size and grows to the full
 * tile on hover — but the click target is always the full size-8 button. Clicking expands it into the
 * step picker, which is **portaled to the document body** and positioned by Floating UI from the "+"
 * anchor: this escapes the pan/zoom viewport's `overflow-hidden` clip (so the menu is never cropped
 * near an edge) and `flip`/`shift` keep it fully on-screen. When another picker is open it renders
 * `inert` (visible, dimmed, not clickable) so the canvas doesn't jump around.
 */
export function SequenceGraphInsert({
  active,
  inert,
  hovered,
  onOpen,
  onPick,
  onPickContainer,
  onCancel,
  disabledReason,
}: Props) {
  const { refs, floatingStyles } = useFloating({
    open: active,
    placement: 'bottom',
    strategy: 'fixed',
    // Re-anchor every frame WHILE OPEN so the menu tracks the "+" as the graph is panned/zoomed via CSS
    // transform (which scroll/resize observers don't emit). The rAF loop only runs while open.
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: PICKER_MIDDLEWARE,
  });

  const disabled = inert === true && !active;
  // Externally-driven hover (from the empty-state hint): show the full grown/blue tile as if hovered.
  const showHover = hovered === true && !active && inert !== true;

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        onClick={onOpen}
        disabled={disabled}
        aria-label="Add a step"
        title="Add a step"
        className={cn('group/add flex size-8 items-center justify-center', disabled ? 'pointer-events-none' : 'cursor-pointer')}>
        <span
          className={cn(
            'flex items-center justify-center rounded-full border border-dashed bg-slate-50 transition-all dark:bg-neutral-900',
            // Active insertion point: a static full tile in blue (the menu floats from it). Otherwise the
            // half-size dashed "+" that grows to the full tile on hover.
            active
              ? GROWN_TILE
              : 'size-4 group-hover/add:size-8 [&>svg]:size-3 group-hover/add:[&>svg]:size-5',
            !active &&
              (inert === true
                ? 'border-lynch-200 text-lynch-300 opacity-40 dark:border-slate-700 dark:text-slate-600'
                : 'border-lynch-300 text-lynch-400 group-hover/add:border-blue-400 group-hover/add:bg-blue-50 group-hover/add:text-blue-500 dark:border-slate-600 dark:text-lynch-500 dark:group-hover/add:border-blue-400 dark:group-hover/add:bg-blue-950 dark:group-hover/add:text-blue-300'),
            // Mirror the hover look when driven externally (twMerge lets these win over the size-4/rest defaults).
            showHover && GROWN_TILE,
          )}>
          <General.Plus />
        </span>
      </button>
      {active && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={floatingStyles} data-sequence-picker className="z-50">
            <SequenceStepPicker
              onPick={onPick}
              onPickContainer={onPickContainer}
              onCancel={onCancel}
              disabledReason={disabledReason}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
